import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/context/ToastContext';
import { useCreateItemList } from '@/api/rpc/hooks/inventory-management/useItemLists';
import {
  CatalogItemNumbersInput,
  CopyValuesButton,
} from '../components/CatalogItemNumbersInput';
import { MAX_CATALOG_ITEM_NUMBERS } from '../components/catalogItemNumbers';
import type { CreateItemListResponse } from '@erp/rpc';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ─── Result summary ───────────────────────────────────────────────────────────

// What the create came back with: how many products the list holds, and which
// pasted values matched nothing — listed and copyable, so they can be chased
// in the source spreadsheet.
function NewListResult({
  result,
  onClose,
}: {
  result: CreateItemListResponse;
  onClose: () => void;
}) {
  const gt = useGT();
  return (
    <>
      <div className="flex flex-col gap-3 py-2">
        <p className="text-sm">
          {gt('{count} products were added to the list.', {
            context: 'new item list result: matched product count',
            count: result.itemCount.toLocaleString(),
          })}
        </p>
        {result.unmatchedInputs.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium">
                {gt('{count} values matched no product and were left out:', {
                  context:
                    'new item list result: heading of the unmatched pasted values',
                  count: result.unmatchedInputs.length.toLocaleString(),
                })}
              </span>
              <CopyValuesButton
                values={result.unmatchedInputs}
                label={gt('Copy', {
                  context: 'button copying the unmatched item numbers',
                })}
              />
            </div>
            <ul className="max-h-48 overflow-y-auto rounded-md border divide-y">
              {result.unmatchedInputs.map((value) => (
                <li key={value} className="px-2 py-1 text-xs font-mono">
                  {value}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button onClick={onClose}>
          <T context="close button on the new item list result view">Done</T>
        </Button>
      </DialogFooter>
    </>
  );
}

// ─── Dialog + form ────────────────────────────────────────────────────────────

/**
 * Build a new org-shared item list from pasted item numbers (whitespace,
 * comma or semicolon separated — an Excel column pastes straight in). The
 * numbers are resolved server-side; the result view reports the matched count
 * and lists the values that matched nothing.
 */
export function NewItemListDialog({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {/* Mount the form only while open so its fields reset on every open. */}
        {open && <NewItemListForm onOpenChange={onOpenChange} />}
      </DialogContent>
    </Dialog>
  );
}

function NewItemListForm({
  onOpenChange,
}: {
  onOpenChange: (open: boolean) => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const createList = useCreateItemList();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [itemNumbers, setItemNumbers] = useState<string[]>([]);
  const [result, setResult] = useState<CreateItemListResponse | null>(null);

  const overCap = itemNumbers.length > MAX_CATALOG_ITEM_NUMBERS;
  const canSubmit =
    name.trim().length > 0 &&
    itemNumbers.length > 0 &&
    !overCap &&
    !createList.isPending;

  const submit = () => {
    createList.mutate(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        source: { type: 'itemNumbers', itemNumbers },
      },
      {
        onSuccess: setResult,
        onError: (error) =>
          showToast({
            title: gt('Error', {
              context: 'create item list error toast title',
            }),
            message:
              error.message ||
              gt('Failed to create the item list.', {
                context: 'fallback error when creating an item list fails',
              }),
            color: 'error',
          }),
      },
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="new item list dialog title">New item list</T>
        </DialogTitle>
        <DialogDescription>
          <T context="new item list dialog subtitle">
            Paste item numbers to build a reusable list, shared with your
            organization.
          </T>
        </DialogDescription>
      </DialogHeader>

      {result ? (
        <NewListResult result={result} onClose={() => onOpenChange(false)} />
      ) : (
        <>
          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-item-list-name">
                <T context="label for the item list name field">Name</T>
              </Label>
              <Input
                id="new-item-list-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={gt('e.g. Copper wire SKUs', {
                  context: 'placeholder for the item list name field',
                })}
                disabled={createList.isPending}
                maxLength={255}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-item-list-description">
                <T context="label for the item list description field">
                  Description
                </T>
              </Label>
              <Textarea
                id="new-item-list-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={gt('Optional notes about this list', {
                  context: 'placeholder for the item list description field',
                })}
                disabled={createList.isPending}
                rows={2}
                maxLength={2000}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>
                <T context="label for the pasted item numbers of a new item list">
                  Item numbers
                </T>
              </Label>
              <CatalogItemNumbersInput
                values={itemNumbers}
                onValuesChange={setItemNumbers}
                notFoundCount={0}
                invalid={overCap}
                className="max-w-full"
              />
              <p className="text-xs text-muted-foreground">
                {overCap
                  ? gt('Up to {max} items per list ({count} entered).', {
                      context:
                        'inline error when a new item list holds more values than allowed',
                      max: MAX_CATALOG_ITEM_NUMBERS.toLocaleString(),
                      count: itemNumbers.length.toLocaleString(),
                    })
                  : gt('{count} values entered.', {
                      context:
                        'running count of the pasted values in the new item list dialog',
                      count: itemNumbers.length.toLocaleString(),
                    })}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createList.isPending}
            >
              <T context="cancel button in the new item list dialog">Cancel</T>
            </Button>
            <Button onClick={() => canSubmit && submit()} disabled={!canSubmit}>
              {createList.isPending ? (
                <T context="submit button label while the item list is being created">
                  Creating…
                </T>
              ) : (
                <T context="submit button label in the new item list dialog">
                  Create list
                </T>
              )}
            </Button>
          </DialogFooter>
        </>
      )}
    </>
  );
}
